import React, { useState } from "react";
import {
  Mail,
  Search,
  Trash2,
  CheckCircle,
  MailOpen,
  Reply,
  Clock,
  Filter,
  User,
  Tag,
  UploadCloud,
  Download,
} from "lucide-react";
import { toggleMessageRead, deleteContactMessage, deleteAllContactMessages, bulkImportContactMessages } from "../../api";
import { useToast } from "../../hooks/use-toast";
import CsvUploadModal from "../../components/admin/CsvUploadModal";
import { toCsv, downloadCsv } from "../../utils/csvHelper";

const ContactMessagesInbox = ({ messages = [], onUpdate }) => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "unread", "read"
  const [expandedId, setExpandedId] = useState(null);
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  const handleImportCsv = async (validItems, rawRows, mode) => {
    const updated = await bulkImportContactMessages(validItems, mode);
    if (onUpdate) onUpdate(updated);
  };

  const handleExportCsv = () => {
    const exportCols = [
      { key: "name", label: "Sender Name" },
      { key: "email", label: "Email" },
      { key: "subject", label: "Subject" },
      { key: "category", label: "Category" },
      { key: "message", label: "Message Content" },
      { key: "isRead", label: "Is Read" },
      { key: "createdAt", label: "Date Received" },
    ];
    const csv = toCsv(messages, exportCols);
    downloadCsv(`inquiries_export_${messages.length}_records.csv`, csv);
    toast({
      title: "Inquiries Exported",
      description: `Exported ${messages.length} messages to CSV.`,
    });
  };

  const handleToggleRead = async (id) => {
    try {
      await toggleMessageRead(id);
      const updated = messages.map((m) => (m.id === id ? { ...m, isRead: !m.isRead } : m));
      if (onUpdate) onUpdate(updated);
    } catch (err) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const handleDelete = async (msg) => {
    if (!window.confirm(`Delete message from ${msg.name}?`)) return;
    try {
      await deleteContactMessage(msg.id);
      const updated = messages.filter((m) => m.id !== msg.id);
      if (onUpdate) onUpdate(updated);
      toast({ title: "Message Deleted", description: "Inquiry has been removed." });
    } catch (err) {
      toast({ title: "Error", description: "Failed to delete message", variant: "destructive" });
    }
  };

  const handleClearAllMessages = async () => {
    if (!window.confirm(`⚠️ Are you sure you want to delete ALL ${messages.length} inquiries? This cannot be undone.`)) return;
    try {
      await deleteAllContactMessages();
      if (onUpdate) onUpdate([]);
      toast({ title: "Messages Cleared", description: "All contact inquiries have been deleted." });
    } catch (err) {
      toast({ title: "Error", description: "Failed to clear messages", variant: "destructive" });
    }
  };

  const filtered = messages.filter((m) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      m.name?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q) ||
      m.subject?.toLowerCase().includes(q) ||
      m.category?.toLowerCase().includes(q) ||
      m.message?.toLowerCase().includes(q);

    if (!matchesSearch) return false;
    if (statusFilter === "unread") return !m.isRead;
    if (statusFilter === "read") return m.isRead;
    return true;
  });

  const unreadCount = messages.filter((m) => !m.isRead).length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
            Customer Inquiries ({messages.length})
            {unreadCount > 0 && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#e5243b] text-white">
                {unreadCount} Unread
              </span>
            )}
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Incoming messages, listing requests, and feedback from the Contact Us form
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Status Tabs */}
          <div className="flex rounded-lg border border-gray-200 bg-white p-1 text-xs">
            <button
              onClick={() => setStatusFilter("all")}
              className={`px-3 py-1.5 rounded-md font-semibold transition-colors ${
                statusFilter === "all" ? "bg-[#e5243b] text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              All ({messages.length})
            </button>
            <button
              onClick={() => setStatusFilter("unread")}
              className={`px-3 py-1.5 rounded-md font-semibold transition-colors ${
                statusFilter === "unread" ? "bg-[#e5243b] text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Unread ({unreadCount})
            </button>
            <button
              onClick={() => setStatusFilter("read")}
              className={`px-3 py-1.5 rounded-md font-semibold transition-colors ${
                statusFilter === "read" ? "bg-[#e5243b] text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Read ({messages.length - unreadCount})
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sender, email, topic..."
              className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#e5243b]"
            />
          </div>

          {/* CSV Actions */}
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button
                type="button"
                onClick={handleClearAllMessages}
                title="Delete all inquiries"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" /> Clear All ({messages.length})
              </button>
            )}
            <button
              type="button"
              onClick={handleExportCsv}
              title="Export inquiries to CSV"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button
              type="button"
              onClick={() => setCsvModalOpen(true)}
              title="Import inquiries from Excel, CSV, TSV, or JSON"
              className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <UploadCloud className="w-3.5 h-3.5" /> Import (Excel / CSV)
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((msg) => {
          const isExpanded = expandedId === msg.id;
          const formattedDate = msg.createdAt
            ? new Date(msg.createdAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "Recent";

          return (
            <div
              key={msg.id}
              className={`bg-white rounded-2xl border transition-all ${
                !msg.isRead ? "border-l-4 border-l-[#e5243b] border-gray-200 shadow-sm" : "border-gray-200"
              }`}
            >
              <div className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm ${
                        !msg.isRead ? "bg-red-50 text-[#e5243b]" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {msg.name ? msg.name.charAt(0).toUpperCase() : <User className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 text-sm">{msg.name}</span>
                        {!msg.isRead && (
                          <span className="w-2 h-2 rounded-full bg-[#e5243b]" title="Unread message" />
                        )}
                        <span className="text-xs text-gray-500">&lt;{msg.email}&gt;</span>
                      </div>
                      <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" /> {formattedDate}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                      <Tag className="w-3 h-3 text-gray-400" /> {msg.category || "General Inquiry"}
                    </span>

                    <button
                      onClick={() => handleToggleRead(msg.id)}
                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title={msg.isRead ? "Mark as Unread" : "Mark as Read"}
                    >
                      {msg.isRead ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4 text-[#e5243b]" />}
                    </button>

                    <a
                      href={`mailto:${msg.email}?subject=Re: ${encodeURIComponent(
                        msg.subject || "Mini Golf Inquiry"
                      )}`}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Reply via email"
                    >
                      <Reply className="w-4 h-4" />
                    </a>

                    <button
                      onClick={() => handleDelete(msg)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete message"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="text-sm font-bold text-gray-900 mb-1.5">{msg.subject}</div>

                <div
                  className={`text-xs sm:text-sm text-gray-600 leading-relaxed ${
                    !isExpanded ? "line-clamp-2" : ""
                  }`}
                >
                  {msg.message}
                </div>

                {msg.message && msg.message.length > 120 && (
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : msg.id)}
                    className="text-xs font-bold text-[#e5243b] hover:text-[#cf1c31] mt-2 inline-block"
                  >
                    {isExpanded ? "Show Less" : "Read Full Message"}
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <MailOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-gray-900">No Messages Found</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
              {search
                ? `No inquiries match your search filter "${search}".`
                : "You have no messages in this view."}
            </p>
          </div>
        )}
      </div>

      <CsvUploadModal
        isOpen={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        sectionKey="messages"
        onImport={handleImportCsv}
        allowModeSwitch={true}
      />
    </div>
  );
};

export default ContactMessagesInbox;
